import React from 'react';
import PropTypes from 'prop-types';
import { Button, makeStyles } from '@material-ui/core';
import ArrowBackIcon from './images/arrow-back.svg';

/**
 * CustomButton for Blue Shield of California
 */
const useStyles = makeStyles(theme => ({
  root: {
    fontSize: '18px',
    fontWeight: 600,
    borderRadius: '32px',
    padding: '15px 32px',
    minWidth: '130px',
    margin: '0 auto',
    display: 'inline-block',
    textTransform: 'none',
    border: 'none',
    '&:hover': {
      background: '#014872',
      color: '#fff',
    },
    '&.Mui-disabled': {
      background: '#b7c9d9',
      color: '#fff',
      opacity: 0.7,
    },
    '@media (max-width: 768px)': {
      minWidth: 'auto',
    },
  },
  backButton: {
    background: 'transparent',
    color: theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: 'none',
    border: 'none',
    padding: '15px 32px',
    minWidth: '130px',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '16px',
    textTransform: 'none',
    '&:hover': {
      background: 'transparent',
      color: '#014872',
    },
    '@media (max-width: 768px)': {
      minWidth: 'auto',
    },
  },
  backIcon: {
    marginRight: 8,
    width: 16,
    height: 16,
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
  },
  // Override styles for ActionButtons integration
  actionButtonOverride: {
    // Reset margins and display for ActionButtons compatibility
    margin: 0,
    display: 'flex',
    border: 'none !important', // Override ActionButtons secondaryAction border
    // Ensure ActionButtons classes take precedence for layout
    '&.MuiButton-root': {
      minWidth: 'unset',
      border: 'none !important',
    },
  },
}));

const formatButtonText = text => {
  if (typeof text !== 'string') return text;
  // Capitalize first letter of each word (title case)
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CustomButton = ({
  children,
  onClick,
  disabled,
  type = 'button',
  className = '',
  style = {},
  ...rest
}) => {
  const classes = useStyles();

  // Check if this button is being used in ActionButtons context
  // ActionButtons uses specific class patterns and ButtonBase-style props
  const isActionButton =
    className.includes('button') ||
    className.includes('Action') ||
    className.includes('primary') ||
    className.includes('secondary') ||
    // Also check if we have ButtonBase-style props that suggest ActionButtons usage
    (Object.prototype.hasOwnProperty.call(rest, 'component') ||
      Object.keys(rest).some(key => key.startsWith('Mui')));

  // Determine button styling approach
  const getButtonClassName = () => {
    if (isActionButton) {
      // For ActionButtons: prioritize external classes, add our override class
      return `${classes.actionButtonOverride} ${className}`;
    }

    // For standalone usage: use our internal styling with back button logic
    if (
      typeof children === 'string' &&
      children.trim().toLowerCase() === 'back'
    ) {
      return `${classes.backButton} ${className}`;
    }

    return `${classes.root} ${className}`;
  };

  // Get button styles
  const getButtonStyles = () => {
    if (isActionButton) {
      // For ActionButtons: respect external styles, minimal override
      return {
        borderRadius: '32px',
        textTransform: 'none',
        fontWeight: 600,
        ...style,
      };
    }

    // For standalone usage: use default styles
    return style;
  };

  // Render special Back button UI if children is 'Back'
  if (
    typeof children === 'string' &&
    children.trim().toLowerCase() === 'back'
  ) {
    return (
      <Button
        type={type}
        className={getButtonClassName()}
        style={getButtonStyles()}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        <span className={classes.buttonContent}>
          <img src={ArrowBackIcon} alt="Back" className={classes.backIcon} />
          {formatButtonText(children)}
        </span>
      </Button>
    );
  }

  return (
    <Button
      type={type}
      className={getButtonClassName()}
      style={getButtonStyles()}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      {isActionButton ? children : formatButtonText(children)}
    </Button>
  );
};

CustomButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default CustomButton;
